import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Card } from '@shared/ui/Card';
import { Table } from '@shared/ui/Table';
import { Badge } from '@shared/ui/Badge';
import { Skeleton } from '@shared/ui/Skeleton';
import { Button } from '@shared/ui/Button';
import {
  RegistryBar,
  RegistryEmpty,
  RegistryFilterSelect,
  RegistryToolbar,
} from '@shared/ui/RegistryShell';

import { useListComposedArchiveQuery, useDeleteComposedMutation } from '@shared/api';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { useAppSelector } from '@app/hooks';
import { useAgentPermission } from '@entities/user';
import { KIND_BADGE, formatComposedDt, formatSize } from '@entities/legal/composed';

/**
 * Архив сгенерированных DOCX.
 *
 * Юристу нужно одно место чтобы найти любой когда-либо собранный
 * черновик: ответ на претензию (response_claim), перевыставление
 * субподрядчику (outgoing_claim), протокол разногласий по договору
 * (protocol_disagreement). До этой страницы DOCX были спрятаны внутри
 * карточек и при вопросе «куда делся ответ на февральскую претензию»
 * приходилось перебирать список вручную.
 *
 * Фильтр по типу + paginated + переход к источнику (карточка
 * претензии/договора). Скачивание через тот же endpoint, что и из
 * карточек — RFC 5987 + кириллица.
 */

const KIND_OPTIONS = [
  { value: '', label: 'Любой тип' },
  { value: 'response_claim', label: 'Ответы на претензию' },
  { value: 'outgoing_claim', label: 'Перевыставления субподрядчику' },
  { value: 'protocol_disagreement', label: 'Протоколы разногласий' },
  { value: 'lawsuit', label: 'Исковые заявления' },
];

const Note = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PageInfo = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-left: ${({ theme }) => theme.spacing(2)};
`;

const Mono = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const PAGE_SIZE = 50;

export function ComposedDocs() {
  const { can_write } = useAgentPermission('statut');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(0);
  const accessToken = useAppSelector((s) => s.auth.accessToken);

  const { data, isLoading, isFetching } = useListComposedArchiveQuery({
    kind: kind || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleDownload = (id: string) => {
    void downloadAuthorizedFile(`/api/v1/agents/statut/composed/${id}/download`, {
      accessToken,
      fallbackName: `Документ-${id}.docx`,
    });
  };

  const [deleteComposed] = useDeleteComposedMutation();
  const handleDelete = async (id: string) => {
    if (!window.confirm('Удалить этот сгенерированный DOCX? Действие необратимо.')) {
      return;
    }
    await deleteComposed(id);
    // WS-event composed_deleted инвалидирует архив автоматически.
  };

  return (
    <Card>
      <Note>
        Все когда-либо сгенерированные DOCX: ответы на претензии, перевыставления субподрядчикам и
        протоколы разногласий по договорам. Скачиваются тем же образом, что и из карточек.
      </Note>

      <RegistryBar>
        <RegistryToolbar>
          <RegistryFilterSelect
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setPage(0);
            }}
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </RegistryFilterSelect>
          <PageInfo>
            {isFetching
              ? 'Загрузка…'
              : total === 0
                ? 'Записей нет'
                : `Показано ${items.length} из ${total}`}
          </PageInfo>
        </RegistryToolbar>
      </RegistryBar>

      {isLoading ? (
        <Skeleton $height={200} />
      ) : items.length === 0 ? (
        <RegistryEmpty>
          Здесь будет архив. Сгенерируйте первый DOCX из карточки претензии или договора, и он
          появится тут.
        </RegistryEmpty>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <th>Тип</th>
                <th>Создан</th>
                <th>Источник</th>
                <th>Размер</th>
                <th>Похожесть</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const badge =
                  it.source_template_kind && KIND_BADGE[it.source_template_kind]
                    ? KIND_BADGE[it.source_template_kind]
                    : { label: it.source_template_kind ?? '—', tone: 'info' as const };
                return (
                  <tr key={it.id}>
                    <td>
                      {/* Тип и дата ведут на страницу документа — там
                          метаданные, скачивание и внешний аудит. */}
                      <Link to={`/agents/statut/documents/${it.id}`}>
                        <Badge $tone={badge.tone}>{badge.label}</Badge>
                      </Link>
                    </td>
                    <td>
                      <Link to={`/agents/statut/documents/${it.id}`}>
                        {formatComposedDt(it.created_at)}
                      </Link>
                    </td>
                    <td>
                      {it.claim_id ? (
                        <Link to={`/agents/statut/claims/${it.claim_id}`}>Претензия</Link>
                      ) : it.contract_id ? (
                        <Link to={`/agents/statut/contracts/${it.contract_id}`}>Договор</Link>
                      ) : (
                        <Mono>—</Mono>
                      )}
                    </td>
                    <td>{formatSize(it.size_bytes)}</td>
                    <td>
                      {it.source_similarity != null
                        ? `${(it.source_similarity * 100).toFixed(0)}%`
                        : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: 8 }}>
                        <Button
                          type="button"
                          $size="sm"
                          $variant="secondary"
                          onClick={() => handleDownload(it.id)}
                        >
                          Скачать
                        </Button>
                        <Button
                          type="button"
                          $size="sm"
                          $variant="ghost"
                          onClick={() => handleDelete(it.id)}
                          disabled={!can_write}
                          title={!can_write ? 'Только чтение' : 'Удалить этот DOCX'}
                        >
                          Удалить
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {pageCount > 1 && (
            <div
              style={{
                display: 'flex',
                gap: 8,
                marginTop: 16,
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              <Button
                type="button"
                $size="sm"
                $variant="secondary"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Назад
              </Button>
              <PageInfo>
                Страница {page + 1} из {pageCount}
              </PageInfo>
              <Button
                type="button"
                $size="sm"
                $variant="secondary"
                disabled={page + 1 >= pageCount}
                onClick={() => setPage((p) => p + 1)}
              >
                Вперёд
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
